import {
  useAdvisorDetailsStore,
  useAppbarStore,
  useDefaultEnumsStore,
  useDirtyFieldsStore,
  useFactFindConfigStore,
  useFactFindErrorStore,
  useFactFindProcessStore,
  useInvestorIDStore,
  useInvoiceDetailStore,
  useMPSConfigStore,
  useNotificationStore,
  useOnboardingStore,
  useOrgDetailsStore,
  useProfileProgressStore,
  useProfileStore,
  useSelectedPlanStore,
  useSubscriptionDetailsStore,
  useSubscriptionPlansStore,
  useUserAccessStore,
  useUserDetailsStore,
  useUserPreferenceStore,
} from '@zeta-core';
import type { IAdvisorDashboard, IAdvisorInfo, IFactFindSectionConfig, IMPSConfig, IUserInfo } from '@zeta-types';
import { setSession } from './utils';

// ----------------------------------------------------------------------

export type SignInParams = {
  email: string;
  password: string;
};

export type SignUpParams = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
};

// Function to reset all zustand stores
const resetAllStores = () => {
  // Reset user-related stores
  useUserDetailsStore.setState({
    user: {} as IUserInfo,
    planningStatus: '',
  });

  useAdvisorDetailsStore.setState({
    advisor: {} as IAdvisorInfo,
    dashboardData: {} as IAdvisorDashboard,
  });

  // Reset subscription-related stores
  useSubscriptionDetailsStore.setState({
    subscription: null,
  });

  useSubscriptionPlansStore.setState({
    subscriptionPlans: [],
  });

  useInvoiceDetailStore.setState({
    invoices: [],
    suspended: false,
  });

  // Reset user access and preferences
  useUserAccessStore.setState({
    userAccess: null,
    loading: true,
  });

  useUserPreferenceStore.setState({
    preference: {
      organisationPreference: { tableView: 0 },
      policyPreference: { tableView: 0 },
      tenantPreference: { tableView: 1 },
      casePreference: { tableDense: false },
      transactionPreference: { tableView: 1 },
      clientPreference: { tableDense: false },
      globalPreference: { mode: 'light', stretch: true, tableDense: false },
      notificationPreference: { tableDense: false },
    },
  });

  // Reset profile and organization stores
  useProfileStore.setState({
    profilePic: '',
    newProfilePic: '',
  });

  useProfileProgressStore.setState({
    progressData: {
      progressPercentage: 10,
      firmProfileProgress: {
        uploadCompanyLogo: false,
        manageFirmRoles: null,
        modelPortfolios: false,
        riskAssessment: false,
        financialPlanning: false,
        adviceTemplates: false,
        productConfig: false,
        factFindFieldConfig: false,
        billingInvoice: false,
      },
      myUserProfileProgress: {
        updateProfilePicture: false,
        addCertificateNExpertise: false,
        addWorkingHours: false,
        setNotificationPref: false,
      },
      firmProfileSetupComplete: false,
    },
    status: false,
  });

  useOrgDetailsStore.setState({
    orgData: {
      response: {
        advisorFirmName: '',
        advisorFCAReferenceNumber: '',
        advisorRegistrationNumber: '',
        website: '',
        email: '',
        countryCode: '',
        mobile: '',
        firmAddress: {
          addressLine1: '',
          addressLine2: '',
          addressLine3: '',
          addressLine4: '',
          addressLine5: '',
          city: '',
          county: '',
          country: '',
          postcode: '',
        },
        firmLogoBytes: '',
        firmLogoDocumentType: '',
      },
    },
    status: false,
  });

  // Reset configuration stores
  useMPSConfigStore.setState({
    mpsConfigDetails: {} as IMPSConfig,
    status: false,
  });

  useFactFindConfigStore.setState({
    factFindConfigDetails: {} as IFactFindSectionConfig,
    status: false,
  });

  useDefaultEnumsStore.setState({
    defaultEnums: {},
    status: false,
  });

  // Reset utility stores
  useDirtyFieldsStore.setState({
    dirtyFieldsState: false,
  });

  useInvestorIDStore.setState({
    investorID: '',
  });

  // Reset selected plan store
  useSelectedPlanStore.getState().clearSelectedPlan();

  // Reset notification and onboarding stores
  useNotificationStore.setState({
    notificationCount: 0,
  });

  useOnboardingStore.setState({
    hideSidebar: false,
  });

  // Reset app bar store
  useAppbarStore.setState({
    sidebarOpen: false,
  });

  // Reset fact find stores
  useFactFindErrorStore.setState({
    ffErrors: null,
  });

  useFactFindProcessStore.setState({
    sections: [],
  });
};

export const signOut = async (): Promise<void> => {
  try {
    // Reset all zustand stores before logout
    resetAllStores();

    // Clear session
    await setSession(null);
  } catch (error) {
    console.error('Error during sign out:', error);
    throw error;
  }
};
